import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Paper, Grid, Chip, Button, Drawer, Divider,
  TextField, Select, MenuItem, FormControl, InputLabel, CircularProgress,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { orderAdditionalProcessService } from '../services/api';

const DEST_OPTIONS = [
  { value: 'packaging', label: 'Склад для пакування' },
  { value: 'finished_goods', label: 'Склад готової продукції' },
  { value: 'qc_stock', label: 'Склад КЯ' },
];

const STATUS_CONFIG = {
  open: { label: 'Відкриті', bg: '#fff3e0', border: '#e65100', color: '#e65100', chipBg: '#e65100' },
  in_progress: { label: 'В роботі', bg: '#e3f2fd', border: '#1565c0', color: '#1565c0', chipBg: '#1565c0' },
  done: { label: 'Виконано', bg: '#e8f5e9', border: '#2e7d32', color: '#2e7d32', chipBg: '#2e7d32' },
};

function fmtDt(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('uk-UA', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function ProcessCard({ proc, onClick }) {
  const cfg = STATUS_CONFIG[proc.queue_status] || STATUS_CONFIG.open;
  const total = proc.order_quantity || 0;
  const remaining = proc.quantity_remaining ?? total;

  return (
    <Paper
      elevation={2}
      onClick={() => onClick(proc)}
      sx={{
        p: 2, cursor: 'pointer', border: `2px solid ${cfg.border}`,
        bgcolor: cfg.bg, borderRadius: 2,
        '&:hover': { boxShadow: 4 },
        transition: 'box-shadow 0.15s',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography sx={{ fontSize: 28 }}>{proc.process_icon}</Typography>
        <Typography variant="h6" fontWeight="bold" color={cfg.color}>{proc.process_name}</Typography>
      </Box>
      <Typography variant="body2" fontWeight="medium">{proc.order_product_name}</Typography>
      {proc.order_product_article && (
        <Typography variant="caption" color="text.secondary">Арт: {proc.order_product_article}</Typography>
      )}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
        <Chip size="small" label={`Станок: ${proc.order_machine_identifier || '—'}`} />
        {proc.order_customer_order_number && (
          <Chip size="small" label={`Замовл: ${proc.order_customer_order_number}`} />
        )}
        <Chip size="small" label={proc.operator_status_display} variant="outlined" />
      </Box>
      {proc.queue_status !== 'done' && (
        <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold', color: cfg.color }}>
          Залишилось: {remaining} / {total} шт
        </Typography>
      )}
      {proc.order_planned_end && (
        <Typography variant="caption" color="text.secondary">
          Плановий кінець: {fmtDt(proc.order_planned_end)}
        </Typography>
      )}
    </Paper>
  );
}

export default function WorkProcessesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const currentUser = (() => { try { return JSON.parse(localStorage.getItem('currentUser') || 'null'); } catch { return null; } })();
  const [reportForm, setReportForm] = useState({ quantity_processed: '', destination: '', comment: '' });
  const [submitting, setSubmitting] = useState(false);
  const [reportError, setReportError] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await orderAdditionalProcessService.getAll({ exclude_self: 1, page_size: 200 });
      setItems(r.data?.results || r.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  const openDrawer = (proc) => {
    setSelected(proc);
    setDrawerOpen(true);
    setReportForm({ quantity_processed: '', destination: '', comment: '' });
    setReportError('');
  };

  const handleTakeWork = async () => {
    if (!selected) return;
    try {
      await orderAdditionalProcessService.setOperatorStatus(selected.id, { operator_status: selected.operator_status });
      setSelected(s => ({ ...s, queue_status: 'in_progress' }));
      await load();
    } catch (e) { console.error(e); }
  };

  const handleReport = async () => {
    setReportError('');
    const qty = parseInt(reportForm.quantity_processed);
    if (!qty || qty <= 0) { setReportError('Вкажіть кількість > 0'); return; }
    if (!reportForm.destination) { setReportError('Оберіть пункт призначення'); return; }
    setSubmitting(true);
    try {
      const r = await orderAdditionalProcessService.report(selected.id, {
        quantity_processed: qty,
        destination: reportForm.destination,
        worker_name: currentUser?.full_name || '',
        comment: reportForm.comment,
      });
      setSelected(r.data);
      setReportForm({ quantity_processed: '', destination: '', comment: '' });
      await load();
    } catch (e) {
      setReportError(e?.response?.data?.detail || 'Помилка');
    } finally {
      setSubmitting(false);
    }
  };

  const open = items.filter(i => i.queue_status === 'open');
  const inProgress = items.filter(i => i.queue_status === 'in_progress');
  const done = items.filter(i => i.queue_status === 'done');

  const columns = [
    { key: 'open', list: open },
    { key: 'in_progress', list: inProgress },
    { key: 'done', list: done },
  ];

  const currentSelected = selected ? (items.find(i => i.id === selected.id) || selected) : null;

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 2, color: '#1565c0' }}>
        Робочі процеси
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2}>
          {columns.map(col => {
            const cfg = STATUS_CONFIG[col.key];
            return (
              <Grid item xs={12} md={4} key={col.key}>
                <Box sx={{ bgcolor: cfg.bg, borderRadius: 2, p: 1.5, minHeight: 200 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: cfg.chipBg }} />
                    <Typography fontWeight="bold" color={cfg.color}>{cfg.label}</Typography>
                    <Chip size="small" label={col.list.length} sx={{ bgcolor: cfg.chipBg, color: '#fff', ml: 'auto' }} />
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {col.list.length === 0 && (
                      <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
                        Немає записів
                      </Typography>
                    )}
                    {col.list.map(proc => (
                      <ProcessCard key={proc.id} proc={proc} onClick={openDrawer} />
                    ))}
                  </Box>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 480 }, p: 3 } }}
      >
        {currentSelected && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" fontWeight="bold">
                {currentSelected.process_icon} {currentSelected.process_name}
              </Typography>
              <IconButton onClick={() => setDrawerOpen(false)}><CloseIcon /></IconButton>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2"><b>Виріб:</b> {currentSelected.order_product_name}</Typography>
              {currentSelected.order_product_article && (
                <Typography variant="body2"><b>Артикул:</b> {currentSelected.order_product_article}</Typography>
              )}
              <Typography variant="body2"><b>Станок:</b> {currentSelected.order_machine_identifier} — {currentSelected.order_machine_name}</Typography>
              {currentSelected.order_customer_order_number && (
                <Typography variant="body2"><b>Замовлення:</b> {currentSelected.order_customer_order_number}</Typography>
              )}
              <Typography variant="body2"><b>Кількість:</b> {currentSelected.order_quantity} шт</Typography>
              <Typography variant="body2">
                <b>Залишилось:</b> {currentSelected.quantity_remaining ?? currentSelected.order_quantity} шт
              </Typography>
              <Typography variant="body2"><b>Статус черги:</b> {currentSelected.queue_status_display}</Typography>
              <Typography variant="body2"><b>Оператор:</b> {currentSelected.operator_status_display}</Typography>
            </Box>

            {currentSelected.queue_status === 'open' && (
              <Button
                variant="outlined"
                fullWidth
                sx={{ mb: 2 }}
                onClick={handleTakeWork}
              >
                Взяти в роботу
              </Button>
            )}

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" sx={{ mb: 1 }}>Звіти</Typography>
            {(currentSelected.reports || []).length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Звітів поки немає</Typography>
            )}
            {(currentSelected.reports || []).map(r => (
              <Box key={r.id} sx={{ mb: 1, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1.5 }}>
                <Typography variant="body2">
                  <b>{r.worker_name || 'Невідомо'}</b>: {r.quantity_processed} шт →{' '}
                  {DEST_OPTIONS.find(d => d.value === r.destination)?.label || r.destination}
                </Typography>
                {r.comment && <Typography variant="caption" color="text.secondary">{r.comment}</Typography>}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{fmtDt(r.created_at)}</Typography>
              </Box>
            ))}

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" sx={{ mb: 1 }}>Подати звіт</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <TextField
                label="Кількість оброблено *"
                type="number"
                value={reportForm.quantity_processed}
                onChange={e => setReportForm(f => ({ ...f, quantity_processed: e.target.value }))}
                inputProps={{ min: 1 }}
                size="small"
              />
              <FormControl size="small" required>
                <InputLabel>Куди відправити *</InputLabel>
                <Select
                  label="Куди відправити *"
                  value={reportForm.destination}
                  onChange={e => setReportForm(f => ({ ...f, destination: e.target.value }))}
                >
                  {DEST_OPTIONS.map(o => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {currentUser?.full_name && (
                <Typography variant="caption" color="text.secondary">
                  Виконавець: <b>{currentUser.full_name}</b>
                </Typography>
              )}
              <TextField
                label="Коментар"
                multiline
                rows={2}
                value={reportForm.comment}
                onChange={e => setReportForm(f => ({ ...f, comment: e.target.value }))}
                size="small"
              />
              {reportError && <Typography color="error" variant="body2">{reportError}</Typography>}
              <Button
                variant="contained"
                onClick={handleReport}
                disabled={submitting || currentSelected.queue_status === 'done'}
                sx={{ bgcolor: '#1565c0' }}
              >
                {submitting ? <CircularProgress size={18} sx={{ mr: 1, color: 'white' }} /> : null}
                Подати звіт
              </Button>
            </Box>
          </Box>
        )}
      </Drawer>
    </Box>
  );
}
